import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  Select,
  ToastDuration,
  ToastType,
  useToast,
} from '@leland-dev/leland-ui-library';
import React, {
  type ComponentPropsWithoutRef,
  type FC,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type FieldError, type SubmitHandler, useForm } from 'react-hook-form';

import {
  type CoachingOutcomeInput,
  GoalName,
  SchoolAdmissionStatus,
} from '../../__generated-gql-types__/globalTypes';
import { DEFAULT_ERROR_MESSAGE } from '../../utils/constants';
import { wasMutationSuccessful } from '../../utils/mutations';
import { GOAL_TO_HEADINGS } from '../../utils/review';
import { ErrorMessage } from '../forms/Inputs';
import Input from '../inputs/Input';
import {
  type CompanyTypeaheadCompany,
  SingleCompanyTypeahead,
} from '../typeahead/CompanyTypeahead';
import {
  type JobTitleTypeaheadJobTitle,
  SingleJobTitleTypeahead,
} from '../typeahead/JobTitleTypeahead';
import {
  type SchoolTypeaheadSchool,
  SingleSchoolTypeahead,
} from '../typeahead/SchoolTypeahead';

import {
  type OutcomeFormCategoryInformationFragment,
  useOutcomeFormCreateCoachingOutcomeMutation,
  useOutcomeFormCreateCompanyMutation,
  useOutcomeFormCreateJobTitleMutation,
} from './__generated-gql-types__/OutcomeForm.generated';

const REGISTER_OPTIONS = Object.freeze({
  CATEGORY_URN: {
    required: true,
  },
  SUB_CATEGORY: (isRequired: boolean, title: string) => ({
    required: { isRequired, message: `${title} is required` },
  }),
  SCHOOL_ADMISSION_INFO: {
    required: true,
  },
  COMPANY_URN: {
    required: true,
  },
  SCORE: {
    required: true,
    valueAsNumber: true,
  },
});

interface OutcomeFormProps {
  categoryInfo: OutcomeFormCategoryInformationFragment[];
  coachName: string;
  defaultValues?: CoachingOutcomeInput;
  onClose: () => void;
  refetchQueries: RefetchQueriesType;
}

export const OutcomeForm: FC<OutcomeFormProps> = ({
  categoryInfo,
  coachName,
  defaultValues,
  onClose,
  refetchQueries,
}) => {
  const { showToast } = useToast();

  const [createCompany] = useOutcomeFormCreateCompanyMutation();
  const [createJobTitle] = useOutcomeFormCreateJobTitleMutation();
  const [createCoachingOutcome, { loading: createCoachingOutcomeInFlight }] =
    useOutcomeFormCreateCoachingOutcomeMutation();

  const {
    register,
    unregister,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitted, isValid },
  } = useForm<CoachingOutcomeInput>({ defaultValues });
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const [selectedCategoryInfo, setSelectedCategoryInfo] = useState<
    Nullable<OutcomeFormCategoryInformationFragment>
  >(
    categoryInfo.find(
      (categoryInfo) => categoryInfo.category.id === defaultValues?.categoryUrn,
    ) ?? null,
  );

  const [selectedSchool, setSelectedSchool] =
    useState<Nullable<SchoolTypeaheadSchool>>(null);
  const [selectedCompany, setSelectedCompany] =
    useState<Nullable<CompanyTypeaheadCompany>>(null);
  const [selectedJobTitle, setSelectedJobTitle] =
    useState<Nullable<JobTitleTypeaheadJobTitle>>(null);

  const isSchoolGoal = selectedCategoryInfo?.category.goal === GoalName.SCHOOL;
  const isCareerGoal = selectedCategoryInfo?.category.goal === GoalName.CAREER;
  const isTestGoal = selectedCategoryInfo?.category.goal === GoalName.TEST;

  const categoryOptions = (categoryInfo ?? []).map((categoryInfo) => ({
    value: categoryInfo,
    label: categoryInfo.category.name,
  }));

  const companyCreateProps: ComponentPropsWithoutRef<
    typeof SingleCompanyTypeahead
  >['createProps'] = useMemo(
    () => ({
      itemType: 'company',
      onCreate: async (name) => {
        const { data } = await createCompany({
          variables: { input: { name } },
        });
        return data?.createCompany ?? { name };
      },
      createableLabel: (input: string) => input,
    }),
    [createCompany],
  );

  const jobTitleCreateProps: ComponentPropsWithoutRef<
    typeof SingleJobTitleTypeahead
  >['createProps'] = useMemo(
    () => ({
      itemType: 'job title',
      onCreate: async (name) => {
        const { data } = await createJobTitle({
          variables: { name },
        });
        return data?.createJobTitle ?? { id: '', name };
      },
      createableLabel: (input: string) => input,
    }),
    [createJobTitle],
  );

  useEffect(() => {
    register('applicantUrn');
    register('categoryUrn', REGISTER_OPTIONS.CATEGORY_URN);
    if (isSchoolGoal) {
      register('schoolAdmissionInfo', REGISTER_OPTIONS.SCHOOL_ADMISSION_INFO);
    }
    if (isCareerGoal) {
      register('jobOfferInfo.companyUrn', REGISTER_OPTIONS.COMPANY_URN);
      register('jobOfferInfo.jobTitleUrn');
    }
    if (isTestGoal) {
      register('testScore');
    }
  }, [register, isSchoolGoal, isCareerGoal, isTestGoal]);

  useEffect(() => {
    if (isSubmitted && !isValid) {
      onClose();
    }
  }, [isSubmitted, isValid, onClose]);

  const unregisterDependentFields = () => {
    unregister('schoolAdmissionInfo');
    unregister('jobOfferInfo.companyUrn');
    unregister('jobOfferInfo.jobTitleUrn');
    unregister('testScore');
    setSelectedSchool(null);
    setSelectedCompany(null);
  };

  const onSubmitWrapper: SubmitHandler<CoachingOutcomeInput> = async (
    values,
  ) => {
    setErrorMessage(null);

    try {
      const res = await createCoachingOutcome({
        variables: {
          input: values,
        },
        refetchQueries,
      });

      if (wasMutationSuccessful(res)) {
        onClose();
        showToast({
          type: ToastType.SUCCESS,
          duration: ToastDuration.NORMAL,
          message: 'Outcome has been added.',
        });
      } else {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        console.warn(res.errors);
      }
    } catch (error) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      console.warn(error);
    }
  };

  return (
    <form
      className="flex grow flex-col overflow-hidden"
      onSubmit={handleSubmit(onSubmitWrapper)}
    >
      <div
        id="review-modal-content"
        className="flex grow flex-col justify-start overflow-y-auto px-6 py-8"
      >
        <p className="text-sm font-medium uppercase tracking-widest text-leland-primary">
          One more thing
        </p>
        <h2
          className={`mt-2 text-3xl font-medium leading-8 text-leland-gray-dark`}
        >
          {selectedCategoryInfo?.category.goal
            ? GOAL_TO_HEADINGS[selectedCategoryInfo.category.goal]
            : `What did ${coachName} help you with?`}
        </h2>
        <p className="mt-2 text-lg text-leland-gray-light">
          Adding this information helps other Leland users see how your coach
          can help them too!
        </p>
        <div className="mt-6">
          <Select<OutcomeFormCategoryInformationFragment>
            label="Category"
            placeholder="Select a category"
            value={selectedCategoryInfo ?? undefined}
            onValueChange={(category) => {
              if (category.category.id) {
                const categoryId = category.category.id;
                unregisterDependentFields();
                setValue('categoryUrn', categoryId);
                setSelectedCategoryInfo(
                  categoryInfo.find(
                    ({ category }) => category.id === categoryId,
                  ) ?? null,
                );
              }
            }}
            options={categoryOptions}
            errorMessage={errors.categoryUrn?.message}
          />
          {isSchoolGoal ? (
            <>
              <SingleSchoolTypeahead
                className="mt-6"
                label="Add the school you were accepted to"
                labelClassName="text-gray-900 text-sm"
                placeholder="Search for a school..."
                value={selectedSchool}
                onChange={(school) => {
                  if (school) {
                    setValue('schoolAdmissionInfo', {
                      schoolUrn: school.id,
                      status: SchoolAdmissionStatus.ADMITTED,
                    });
                    setSelectedSchool(school);
                  }
                }}
                error={errors.schoolAdmissionInfo as FieldError}
              />
            </>
          ) : null}
          {isCareerGoal ? (
            <>
              <SingleCompanyTypeahead
                className="mt-6"
                label="Add the company you landed a role at"
                placeholder="Search for a company..."
                value={selectedCompany}
                categoryUrn={selectedCategoryInfo?.category.id}
                createProps={companyCreateProps}
                onChange={(company) => {
                  if (company?.id) {
                    setValue('jobOfferInfo.companyUrn', company.id);
                    setSelectedCompany(company);
                  }
                }}
                error={errors.jobOfferInfo?.companyUrn}
              />
              <SingleJobTitleTypeahead
                className="mt-6"
                label="Role Landed"
                placeholder="Search for a job title..."
                value={selectedJobTitle}
                createProps={jobTitleCreateProps}
                onChange={(jobTitle) => {
                  if (jobTitle?.id) {
                    setValue('jobOfferInfo.jobTitleUrn', jobTitle.id);
                    setSelectedJobTitle(jobTitle);
                  }
                }}
                error={errors.jobOfferInfo?.companyUrn}
              />
            </>
          ) : null}
          {isTestGoal ? (
            <Input
              className="mt-6"
              label="Score"
              placeholder="Write your score..."
              type="number"
              register={register('testScore.score', REGISTER_OPTIONS.SCORE)}
              onChange={() =>
                setValue(
                  'testScore.categoryUrn',
                  selectedCategoryInfo.category.id,
                )
              }
              error={errors.testScore?.score}
            />
          ) : null}
        </div>
      </div>
      <div className="flex w-full items-center justify-between space-x-2 border-t border-leland-gray-stroke p-4  md:rounded-b-lg">
        <Button
          type="button"
          label="Skip"
          buttonColor={ButtonColor.WHITE}
          size={ButtonSize.LARGE}
          width={ButtonWidth.FULL_MOBILE}
          disabled={createCoachingOutcomeInFlight}
          onClick={onClose}
          id="outcome_form:skip"
        />
        <Button
          type="submit"
          label="Submit"
          buttonColor={ButtonColor.PRIMARY}
          size={ButtonSize.LARGE}
          width={ButtonWidth.FULL_MOBILE}
          disabled={createCoachingOutcomeInFlight}
          id="outcome_form:submit"
        />
        {errorMessage ? <ErrorMessage errorMessage={errorMessage} /> : null}
      </div>
    </form>
  );
};
